import React, { useState, useEffect, useRef } from 'react';
import { countries, detectCountryByIP, formatPhoneWithCountry, validatePhoneNumber } from '../utils/countryPhoneCodes';
import './PhoneInput.css';

function PhoneInput({ value, onChange, disabled, placeholder, required }) {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [showValidation, setShowValidation] = useState(false);
  const dropdownRef = useRef(null);
  const selectorRef = useRef(null);
  const validationTimer = useRef(null);
  /**
   * El último número que ESTE componente le avisó al padre.
   *
   * Sirve para distinguir un `value` que es el eco de lo que acabamos de emitir
   * —hay que ignorarlo— de uno que llegó de afuera, por ejemplo un
   * autocompletado, que hay que ingerir. Sin esa distinción no hay forma de
   * saber si un `value` distinto del estado interno es algo nuevo que entra o
   * algo viejo que el padre todavía no actualizó.
   */
  const ultimoEmitido = useRef(null);

  // Detectar país al cargar el componente
  useEffect(() => {
    detectCountryByIP().then(country => {
      setSelectedCountry(country);
    });
  }, []);

  // Manejar clics fuera del dropdown y recalcular posición
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          selectorRef.current && !selectorRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    function handleScroll() {
      if (showDropdown) {
        calculateDropdownPosition();
      }
    }

    function handleResize() {
      if (showDropdown) {
        calculateDropdownPosition();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      // Limpiar timer de validación
      if (validationTimer.current) {
        clearTimeout(validationTimer.current);
      }
    };
  }, [showDropdown]);

  /**
   * Avisarle al padre el número completo cuando cambia el país o los dígitos.
   *
   * ACÁ ESTABA EL BUG DEL TELÉFONO QUE NO SE GUARDABA. Esto decía:
   *
   *     } else if (!phoneNumber) { onChange(''); }
   *
   * o sea que en el PRIMER render, con el estado interno todavía vacío, le
   * mandaba `''` al padre. Este componente se monta recién cuando el formulario
   * abre el resto de los campos —justo después del autocompletado por DNI—, así
   * que su primer acto era borrar el teléfono que se acababa de completar. El
   * dato se guardaba bien en la base; se perdía al traerlo de vuelta.
   *
   * Hay una segunda carrera atrás de esa: el país se detecta con un pedido
   * asíncrono (`detectCountryByIP`), así que un `value` que llega antes de que
   * ese pedido termine no se puede parsear todavía. Si mientras tanto se emite
   * `''`, cuando el país por fin llega ya no queda nada que parsear.
   *
   * De ahí las dos guardas de abajo.
   */
  useEffect(() => {
    const completo = selectedCountry && phoneNumber
      ? formatPhoneWithCountry(phoneNumber, selectedCountry)
      : '';

    // Vino un número de afuera que todavía no se pudo ingerir (falta el país).
    // No se toca: borrarlo acá es perderlo para siempre.
    const pendienteDeIngerir =
      Boolean(value) && !phoneNumber && value !== ultimoEmitido.current;
    if (pendienteDeIngerir) return;

    // Ya coinciden: emitir sería un renderizado de más y un eco innecesario.
    if (completo === (value || '')) return;

    ultimoEmitido.current = completo;
    onChange(completo);
  }, [selectedCountry, phoneNumber, value, onChange]);

  /**
   * Ingerir un `value` que vino de afuera (el autocompletado por DNI, o un
   * formulario que arranca con un número ya cargado).
   *
   * Se saltea el eco de lo que emitimos nosotros: sin eso, cada tecla dispararía
   * un re-parseo del número que acabamos de armar.
   */
  useEffect(() => {
    if (value === ultimoEmitido.current) return;
    if (value && selectedCountry) {
      // Intentar extraer el número sin el código del país
      const dialCodeClean = selectedCountry.dialCode.replace(/\D/g, '');
      const valueClean = value.replace(/\D/g, '');
      
      if (valueClean.startsWith(dialCodeClean)) {
        setPhoneNumber(valueClean.substring(dialCodeClean.length));
      } else {
        setPhoneNumber(valueClean);
      }
    }
  }, [value, selectedCountry]);

  // Función para calcular la posición del dropdown
  const calculateDropdownPosition = () => {
    if (selectorRef.current) {
      const rect = selectorRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const dropdownHeight = 300; // max-height del dropdown
      
      // Calcular si hay espacio suficiente abajo
      const spaceBelow = windowHeight - rect.bottom;
      const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
      
      setDropdownPosition({
        top: showAbove ? rect.top - dropdownHeight - 5 : rect.bottom + 5,
        left: rect.left,
        width: rect.width
      });
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setShowDropdown(false);
    setSearchTerm('');
  };

  const handleToggleDropdown = () => {
    if (!showDropdown) {
      calculateDropdownPosition();
    }
    setShowDropdown(!showDropdown);
  };

  const handlePhoneChange = (e) => {
    // Solo permitir números
    const value = e.target.value.replace(/\D/g, '');
    setPhoneNumber(value);
    
    // Resetear validación mientras escribe
    setShowValidation(false);
    
    // Limpiar timer anterior
    if (validationTimer.current) {
      clearTimeout(validationTimer.current);
    }
    
    // Mostrar validación después de 1.5 segundos de inactividad
    if (value.length >= 6) {
      validationTimer.current = setTimeout(() => {
        setShowValidation(true);
      }, 1500);
    }
  };

  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    country.dialCode.includes(searchTerm) ||
    country.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Validar el número actual solo si se debe mostrar la validación
  const shouldValidate = showValidation && phoneNumber && phoneNumber.length >= 7;
  const isValid = selectedCountry && shouldValidate ? 
    validatePhoneNumber(phoneNumber, selectedCountry) : true;

  if (!selectedCountry) {
    return <div className="phone-input-loading">Cargando...</div>;
  }

  return (
    <div className="phone-input-container">
      <div className="phone-input-wrapper">
        <div 
          ref={selectorRef}
          className="country-selector"
          onClick={handleToggleDropdown}
        >
          <span className="country-flag">{selectedCountry.flag}</span>
          <span className="country-code">{selectedCountry.dialCode}</span>
          <svg className="dropdown-arrow" width="10" height="6" viewBox="0 0 10 6">
            <path d="M0 0L5 6L10 0" fill="currentColor"/>
          </svg>
        </div>
        
        <input
          type="tel"
          className={`phone-number-input ${!isValid && phoneNumber ? 'invalid' : ''}`}
          placeholder={placeholder || "Tu número sin código de país"}
          value={phoneNumber}
          onChange={handlePhoneChange}
          disabled={disabled}
          required={required}
        />
      </div>
      
      {showDropdown && (
        <>
          <div className="dropdown-backdrop" onClick={() => setShowDropdown(false)} />
          <div 
            className="country-dropdown" 
            ref={dropdownRef}
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              minWidth: `${Math.max(dropdownPosition.width, 280)}px`
            }}
          >
          <div className="country-search">
            <input
              type="text"
              placeholder="Buscar país..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
          <div className="country-list">
            {filteredCountries.map(country => (
              <div
                key={country.code}
                className={`country-item ${selectedCountry.code === country.code ? 'selected' : ''}`}
                onClick={() => handleCountrySelect(country)}
              >
                <span className="country-flag">{country.flag}</span>
                <span className="country-name">{country.name}</span>
                <span className="country-dial-code">{country.dialCode}</span>
              </div>
            ))}
          </div>
        </div>
        </>
      )}
      
      {!isValid && shouldValidate && (
        <div className="phone-error">
          Número inválido para {selectedCountry.name}
        </div>
      )}
    </div>
  );
}

export default PhoneInput;